"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Loader,
} from "@devhop/ui";
import { MapPin, MoveRight } from "lucide-react";
import { useMemo, useState } from "react";
import {
  useAssignInventoryLocationMutation,
  useSkuLocationBalancesQuery,
} from "../api/queries";
import { ShopWarehouseLocationPickerRemote } from "./ShopWarehouseLocationPicker";

type Props = {
  shopId: string;
  branchId: string;
  shopProductSkuId: string;
  trackingMode: string;
  canAdjust: boolean;
};

export function ShopInventoryLocationDistributionPanel({
  shopId,
  branchId,
  shopProductSkuId,
  trackingMode,
  canAdjust,
}: Props) {
  const tm = trackingMode === "serial" ? "lot_serial" : trackingMode;
  const balancesQ = useSkuLocationBalancesQuery(
    shopId,
    branchId,
    shopProductSkuId,
  );
  const assignMut = useAssignInventoryLocationMutation(shopId);

  const [assignOpen, setAssignOpen] = useState(false);
  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [qty, setQty] = useState(1);
  const [useUnlocated, setUseUnlocated] = useState(true);

  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);
  const data = balancesQ.data;

  if (tm !== "none") {
    return (
      <section className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-muted-foreground text-sm">
        <MapPin className="mb-1 inline size-4" aria-hidden />
        {" "}
        ການຈັດຊ່ອງສາງສຳລັບ SKU ໂໝດ lot/serial ຈະມາໃນ phase ຖັດໄປ
      </section>
    );
  }

  async function handleAssign() {
    if (!toLocationId || qty < 1) return;
    await assignMut.mutateAsync({
      branchId,
      shopProductSkuId,
      fromLocationId: useUnlocated ? null : fromLocationId || null,
      toLocationId,
      qty,
    });
    setAssignOpen(false);
    setFromLocationId("");
    setToLocationId("");
    setQty(1);
    setUseUnlocated(true);
  }

  return (
    <section className="rounded-xl border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-border border-b px-4 py-3">
        <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground text-lg">
          <MapPin className="size-5" aria-hidden />
          ກະจายຕຳແໜ່ງ
        </h2>
        {canAdjust ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setAssignOpen(true)}
          >
            <MoveRight className="size-4" aria-hidden />
            ຍ້າຍຊ່ອງ
          </Button>
        ) : null}
      </div>

      {balancesQ.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : balancesQ.isError ? (
        <p className="px-4 py-6 text-destructive text-sm">ໂຫຼດຕຳແໜ່ງລົ້ມເຫຼວ</p>
      ) : (
        <div className="space-y-3 px-4 py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">on_hand</p>
              <p className="font-semibold tabular-nums">
                {numberFmt.format(data?.onHand ?? 0)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">ຈັດຊ່ອງແລ້ວ</p>
              <p className="font-semibold tabular-nums">
                {numberFmt.format(data?.locatedTotal ?? 0)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">ຍັງບໍ່ຈັດຊ່ອງ</p>
              <p className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                {numberFmt.format(data?.unlocatedQty ?? 0)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">ຊ່ອງທີ່ມີ qty</p>
              <p className="font-semibold tabular-nums">
                {numberFmt.format(data?.locations.length ?? 0)}
              </p>
            </div>
          </div>

          {(data?.locations.length ?? 0) === 0 ? (
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີ qty ໃນຊ່ອງ — ຮັບເຂົ້າພ້ອມ default location ຫຼື ຍ້າຍຈາກ unlocated
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[360px] text-sm">
                <thead className="border-border border-b text-muted-foreground text-xs">
                  <tr>
                    <th className="py-2 text-start font-medium">ໂຊນ</th>
                    <th className="py-2 text-start font-medium">ຕຳແໜ່ງ</th>
                    <th className="py-2 text-end font-medium">qty</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.locations.map((loc) => (
                    <tr
                      key={loc.locationId}
                      className="border-border border-b last:border-0"
                    >
                      <td className="py-2">{loc.zoneCode}</td>
                      <td className="py-2 font-mono">{loc.locationCode}</td>
                      <td className="py-2 text-end tabular-nums">
                        {numberFmt.format(loc.qtyOnHand)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ຍ້າຍ qty ໄປຊ່ອງ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={useUnlocated}
                  onChange={() => setUseUnlocated(true)}
                />
                ຈາກຍັງບໍ່ຈັດຊ່ອງ
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={!useUnlocated}
                  onChange={() => setUseUnlocated(false)}
                />
                ຈາກຊ່ອງ
              </label>
            </div>
            {!useUnlocated ? (
              <div>
                <Label>ຊ່ອງຕົ້ນທາງ</Label>
                <ShopWarehouseLocationPickerRemote
                  shopId={shopId}
                  branchId={branchId}
                  value={fromLocationId}
                  onValueChange={setFromLocationId}
                  className="mt-1 w-full"
                />
              </div>
            ) : null}
            <div>
              <Label>ຊ່ອງປາຍທາງ</Label>
              <ShopWarehouseLocationPickerRemote
                shopId={shopId}
                branchId={branchId}
                value={toLocationId}
                onValueChange={setToLocationId}
                className="mt-1 w-full"
              />
            </div>
            <div>
              <Label htmlFor="assign-qty">ຈຳນວນ</Label>
              <Input
                id="assign-qty"
                type="number"
                min={1}
                max={999_999}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 1)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={handleAssign}
              disabled={assignMut.isPending || !toLocationId}
            >
              ຢືນຢັນ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
